import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Pagination } from "@customTypes/index";
import { PromoCode } from "@customTypes/promoCode";

import { promoCodeQKeys } from "./queryKeys";


interface QueryProps {
  pagination?: Partial<Pagination>;
  search?: string;
  activeOnly?: boolean;
  service?: string;
  medicalProfessional?: string;
  isEnabled?: boolean;
}

const queryFn = ({ pagination, search, activeOnly, service, medicalProfessional }: QueryProps) => {
  return api.get("promocodes/", {
    params: {
      ...(pagination && {
        page: pagination.page,
        page_size: pagination.pageSize,
      }),
      ...(search && { search }),
      ...(activeOnly !== undefined && { active_only: activeOnly }),
      ...(service && { service }),
      ...(medicalProfessional && { medical_professional: medicalProfessional }),
    },
  });
};

const select = (res: any) => {
  // console.log(res.data);

  // Check if response has pagination structure
  if (res.data.data) {
    const pagination: Pagination = {
      page: res.data.pagination.page,
      pageCount: res.data.pagination.page_count,
      pageSize: res.data.pagination.page_size,
      total: res.data.pagination.total,
    };

    const newData: PromoCode[] = [];

    for (const i in res.data.data) {
      const { 
        code, 
        discount_percent, 
        created_at, 
        valid_from, 
        valid_until, 
        services, 
        medical_professional, 
        max_uses, 
        current_uses 
      } = res.data.data[i];

      newData.push({
        code,
        discountPercent: discount_percent || 0,
        validFrom: new Date(valid_from),
        validUntil: new Date(valid_until),
        services: services || [],
        medicalProfessional: medical_professional || "",
        maxUses: max_uses || 0,
        currentUses: current_uses || 0,
        createdAt: new Date(created_at),
      });
    }

    return {
      newData,
      pagination,
    };
  }

  // Fallback for non-paginated response
  if (Array.isArray(res.data) && res.data.length) {
    const newData: PromoCode[] = [];

    for (const i in res.data) {
      const { 
        code, 
        discount_percent, 
        created_at, 
        valid_from, 
        valid_until, 
        services, 
        medical_professional, 
        max_uses, 
        current_uses 
      } = res.data[i];

      newData.push({
        code,
        discountPercent: discount_percent || 0,
        validFrom: new Date(valid_from),
        validUntil: new Date(valid_until),
        services: services || [],
        medicalProfessional: medical_professional || "",
        maxUses: max_uses || 0,
        currentUses: current_uses || 0,
        createdAt: new Date(created_at),
      });
    }

    return {
      newData,
      pagination: {
        page: 1,
        pageCount: 1,
        pageSize: 25, // Use consistent default pageSize
        total: newData.length,
      },
    };
  }

  return {
    newData: [],
    pagination: {
      page: 1,
      pageCount: 1,
      pageSize: 25, // Use consistent default pageSize
      total: 0,
    },
  };
};

export const useGetPromoCodes = ({
  pagination,
  search,
  activeOnly,
  service,
  medicalProfessional,
  isEnabled = true,
}: QueryProps = {}) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: [
      ...promoCodeQKeys.allWithFilters({
        pagination,
        search,
        activeOnly,
        service,
        medicalProfessional,
      }),
    ],
    queryFn: () => queryFn({ pagination, search, activeOnly, service, medicalProfessional }),
    select,
    staleTime: Infinity,
    enabled: isEnabled,

    onError: (res: any) => {
      console.error("[From Get All Promo Codes]", res);
      handleErrors(res, "query");
    },
  });
};
